import backArrow from '../../assets/img/backArrow.png';
import { useState, useRef, useEffect, useContext } from 'react';
import { Spin, message } from 'antd';
import './EmailInput.css';
import api from 'api';
import { get } from 'utils/fetch';
import PdfIcon from '../../assets/img/PdfIcon.png';
import { FaPaperclip } from 'react-icons/fa';
import { getMimeType, getFileIcon, isPhoneSize } from 'utils/util';
import { formatCurrency, USDollar } from 'utils/format';
import { GlobalContext } from 'GlobalContext';

export function InvestorReportEmail(props) {
    const { setView, setEmailResponse, investorId } = props;
    const { accessToken, refreshSession } = useContext(GlobalContext);
    const [messageApi, contextHolder] = message.useMessage();
    const [inputEmail, setInputEmail] = useState('');
    const [subject, setSubject] = useState('');
    const textAreaRef = useRef(null);
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(true);
    const [files, setFiles] = useState([]);
    const [investorName, setInvestorName] = useState([]);
    const [investorEmail, setInvestorEmail] = useState([]);
    const [investorSummaryDetails, setInvestorSummaryDetails] = useState(null);

    const displayErrorMessage = (message) => {
        messageApi.open({
            type: 'error',
            content: message,
        });
    };

    async function fetchPdf() {
        // Fetch the PDF from the FastAPI backend
        fetch(api.email.pdfTemplate, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/pdf',
                Authorization: `Bearer ${accessToken}`,
            },
        })
            .then((response) => {
                // Get filename from Content-Disposition header
                const contentDisposition = response.headers.get(
                    'Content-Disposition'
                );
                refreshSession();
                // console.log(contentDisposition)
                const matches =
                    contentDisposition &&
                    contentDisposition.match(/filename=(.+)/);
                // console.log(contentDisposition.match(/filename=(.+)/))
                const filename = matches ? matches[1] : 'default.pdf'; // Fallback if no filename
                return response.blob().then((blob) => {
                    const url = URL.createObjectURL(blob);
                    setFiles([...files, { filename, url, blob }]);
                });
            })
            .catch((err) => console.error('Error fetching PDF:', err));
    }

    async function fetchInvestorSummaryDetails(investorId) {
        await get(
            api.email.investorSummaryDetails(investorId),
            setInvestorSummaryDetails,
            (errors) =>
                errors.forEach((err) => {
                    displayErrorMessage(err.message);
                }),
            accessToken
        );
        refreshSession();
        setLoading(false);
    }

    function setReceiptSubject() {
        setSubject(`Investment Summary of ${investorName}`);
    }
    function setReceiptContent() {
        setContent(`Dear Investor,

Thank you for your continued trust and partnership with AlphaX RE Capital. 

Below is your latest investment summary with AlphaX RE Capital. We appreciate your ongoing support and remain committed to delivering transparency and strong performance. 

For more detailed and visual information, please visit our Investor Portal at: https://portal.alphax-capital.com.

If you haven't logged in before, please sign up using the email address where you received this message and create your own password. 

If you have any questions or would like to discuss any details further, please don't hesitate to reach out to our team.

Warm regards,
AlphaX RE Capital`);
    }

    async function fetchReceiptPdf() {
        const body = JSON.stringify({
            investorName,
            investorId,
        });
        // Fetch the PDF from the FastAPI backend
        fetch(api.email.investorSummaryPdf, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/pdf',
                Authorization: `Bearer ${accessToken}`,
            },
            body,
        })
            .then((response) => {
                // Get filename from Content-Disposition header
                const contentDisposition = response.headers.get(
                    'Content-Disposition'
                );
                refreshSession();
                // console.log(contentDisposition)
                const matches =
                    contentDisposition &&
                    contentDisposition.match(/filename="(.+)"/);
                // console.log(contentDisposition.match(/filename=(.+)/))
                const filename = matches ? matches[1] : 'default.pdf'; // Fallback if no filename
                return response.blob().then((blob) => {
                    const url = URL.createObjectURL(blob);
                    setFiles([{ filename, url, blob }]);
                });
            })
            .then(() => setLoading(false))
            .catch((err) => console.error('Error fetching PDF:', err));
    }

    useEffect(() => {
        // set up receipt text
        if (investorName.length) {
            setReceiptSubject();
            setReceiptContent(); // Only set content when all the necessary data is loaded
            fetchReceiptPdf();
        }
    }, [investorName]); // Watch for changes to these values

    function restore() {
        if (!investorSummaryDetails) return;
        setInvestorEmail([investorSummaryDetails.email]);
        setInvestorName(investorSummaryDetails.legal_name);
    }

    useEffect(() => {
        restore();
    }, [investorSummaryDetails]);

    async function setUp() {
        // fetch detail: name and email
        fetchInvestorSummaryDetails(investorId);
    }

    useEffect(() => {
        if (accessToken === '') return;
        // set up subject, content, file
        setUp();
    }, [accessToken]);

    // Handle input change
    const handleInputChange = (e) => {
        setInputEmail(e.target.value);
    };

    // Handle key down (when pressing "Enter")
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && inputEmail.trim() !== '') {
            // Basic email validation
            if (validateEmail(inputEmail.trim())) {
                setInvestorEmail([...investorEmail, inputEmail.trim()]);
                setInputEmail(''); // Clear input after adding email
            } else {
                alert('Please enter a valid email address');
            }
            e.preventDefault();
        }
    };

    // Validate email using a basic regex
    const validateEmail = (email) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    };

    // Remove email from the list
    const removeEmail = (index) => {
        setInvestorEmail(investorEmail.filter((_, i) => i !== index));
    };

    useEffect(() => {
        // Adjust the height based on the scroll height
        if (textAreaRef.current) {
            textAreaRef.current.style.height = 'auto'; // Reset height to auto
            textAreaRef.current.style.height =
                textAreaRef.current.scrollHeight + 'px'; // Set to scroll height
        }
    }, [content]); // Re-run whenever the content changes
    // console.log(fromId)
    async function sendEmail() {
        if (investorEmail.length === 0) {
            alert('Please add recipients');
            return;
        }
        setEmailResponse('Sending...');
        setView('investor');
        // Create a FormData object
        const formData = new FormData();
        // formData.append('files', files.map(file => new File([file.blob], file.filename, { type: 'application/pdf' })))
        files.map((file) =>
            formData.append(
                'files',
                new File([file.blob], file.filename, {
                    type: getMimeType(file.filename),
                })
            )
        );
        // formData.append('to_email', emails);  // Append the recipient's email
        investorEmail.map((email) =>
            formData.append('to_emails', email.trim())
        );
        formData.append('subject', subject); // Append the email subject
        formData.append('content', content.replace(/\n/g, '<br>\n')); // Append the email content
        formData.append('investor_id', investorId);

        // Send the email with the PDF attachment
        const emailResponse = await fetch(api.email.sendInvestorSummaryEmail, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: formData,
        });
        refreshSession();

        const emailResult = await emailResponse.json();
        // console.log(emailResult);
        setEmailResponse(emailResult.message);
        // Hide the message after 1 second
        setTimeout(() => {
            setEmailResponse('');
        }, 1000);
        // console.log(emailResult);
    }
    // console.log(content)
    // console.log(content.replace(/\n/g, '<br>\n'))
    const handleFileChange = (e) => {
        const file = e.target.files[0]; // Get the first selected file
        if (file) {
            // Generate a temporary URL for the file
            const tempUrl = URL.createObjectURL(file);
            setFiles([
                ...files,
                {
                    filename: e.target.files[0].name,
                    url: tempUrl,
                    blob: e.target.files[0],
                },
            ]); // Allow multiple files to be uploaded
        }
    };

    const triggerFileInput = () => {
        document.getElementById('fileInput').click(); // Trigger file input click
    };

    return (
        <>
            {loading && (
                <div style={{ marginTop: '30px', textAlign: 'center' }}>
                    <Spin />
                </div>
            )}

            {!loading && (
                <>
                    <div style={{ margin: '20px' }}>
                        <img
                            style={{
                                width: '15px',
                                height: '20px',
                                cursor: 'pointer',
                            }}
                            src={backArrow}
                            onClick={() => setView('investor')}
                        ></img>
                    </div>
                    <div
                        style={
                            isPhoneSize()
                                ? { height: 'calc(100vh - 278px)' }
                                : { height: 'calc(100vh - 115px)' }
                        }
                    >
                        <div style={{ height: '100%', overflowY: 'auto' }}>
                            <div
                                className="email-input-container"
                                style={{ width: '100%' }}
                            >
                                <div
                                    className="emails"
                                    style={{ width: '100%' }}
                                >
                                    {investorEmail.map((email, index) => (
                                        <div key={index} className="email-chip">
                                            {email}
                                            <span
                                                className="remove-btn"
                                                onClick={() =>
                                                    removeEmail(index)
                                                }
                                            >
                                                &times;
                                            </span>
                                        </div>
                                    ))}
                                    <input
                                        type="text"
                                        value={inputEmail}
                                        onChange={handleInputChange}
                                        onKeyDown={handleKeyDown}
                                        placeholder="Enter email addresses"
                                        className="email-input"
                                        style={{
                                            width: `${Math.max(
                                                20,
                                                inputEmail.length * 10
                                            )}px`,
                                        }} // Dynamically resize input
                                    />
                                </div>
                            </div>

                            <div
                                className="email-input-container"
                                style={{ width: '100%' }}
                            >
                                <div
                                    className="emails"
                                    style={{ width: '100%' }}
                                >
                                    <input
                                        type="text"
                                        value={subject}
                                        onChange={(e) =>
                                            setSubject(e.target.value)
                                        }
                                        // onKeyDown={handleKeyDown}
                                        placeholder="Subject..."
                                        className="email-input"
                                        style={{ width: `100%` }} // Dynamically resize input
                                    />
                                </div>
                            </div>

                            <div
                                className="email-input-container"
                                style={{ width: '100%' }}
                            >
                                <div
                                    className="emails"
                                    style={{ width: '100%' }}
                                >
                                    <textarea
                                        type="text"
                                        ref={textAreaRef}
                                        value={content}
                                        onChange={(e) =>
                                            setContent(e.target.value)
                                        }
                                        // onKeyDown={handleKeyDown}
                                        placeholder="Content..."
                                        className="email-input"
                                        style={{
                                            width: `100%`,
                                            minHeight: '500px',
                                            fontFamily: 'Arial, sans-serif',
                                        }} // Dynamically resize input
                                    />
                                </div>
                            </div>

                            {files && (
                                <div>
                                    {files.map((file, idx) => (
                                        <div
                                            key={idx}
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                            }}
                                        >
                                            <a
                                                href={file.url}
                                                target="_blank"
                                                rel="noreferrer noopener"
                                            >
                                                {/* <iframe src={pdfUrl} width="100%" height="600px" title="PDF Preview" /> */}
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        fontSize: '15px',
                                                        color: 'black',
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            padding: '5px',
                                                            paddingBottom: '0',
                                                            fontSize: '20px',
                                                        }}
                                                    >
                                                        {getFileIcon(
                                                            file.filename
                                                        )}
                                                        {/* <img src={PdfIcon} style={{ width: "14px" }}></img> */}
                                                    </div>
                                                    {file.filename}
                                                </div>
                                            </a>
                                            <span
                                                style={{
                                                    marginLeft: '10px',
                                                    alignContent: 'center',
                                                    fontWeight: 'bold',
                                                    color: '#FF0000',
                                                    fontSize: '15px',
                                                    cursor: 'pointer',
                                                }}
                                                className="remove-btn"
                                                onClick={() =>
                                                    setFiles(
                                                        files.filter(
                                                            (_, i) => i !== idx
                                                        )
                                                    )
                                                }
                                            >
                                                &times;
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div style={{ display: 'flex' }}>
                                <button
                                    style={{
                                        height: '30px',
                                        width: '50px',
                                        borderRadius: '30px',
                                        background: 'rgb(17 32 86)',
                                        color: 'white',
                                        cursor: 'pointer',
                                    }}
                                    onClick={sendEmail}
                                >
                                    Send
                                </button>

                                <div
                                    style={{
                                        alignSelf: 'center',
                                        marginLeft: '10px',
                                    }}
                                >
                                    <FaPaperclip
                                        style={{
                                            cursor: 'pointer',
                                            fontSize: '24px',
                                            color: '#4A90E2',
                                        }}
                                        onClick={triggerFileInput}
                                    />
                                </div>
                            </div>

                            {/* Hidden file input */}
                            <input
                                id="fileInput"
                                type="file"
                                multiple
                                onChange={handleFileChange}
                                style={{ display: 'none' }} // Hide the default file input
                            />
                        </div>
                    </div>
                </>
            )}
        </>
    );
}
